import React, { useState, useEffect } from "react";

import { MultiSelect } from "@learningblocks/shared-components/lib/Questions/MultiSelect";

import Arrow from "./images/hint-arrow.svg";
import whiteX from "./images/white-x.svg";

import parse from "html-react-parser";
import { useTranslation } from "react-i18next";
import { default as i18n } from "@learningblocks/shared-components/lib/i18n";

import "./index.css";

export default function MSQuestion(props) {
  const {
    t,
    // i18n
  } = useTranslation("translation");

  const [data, setData] = useState(props.data);
  const options = props.data.options;
  const correctIds = data.answerIds;
  const image = data.image;
  const [imgSrc, setImgSrc] = useState(
    props.preview
      ? data.imageURL
      : window.location.origin +
          window.location.pathname +
          `locales/${i18n.language}/images/${image}`
  );

  const [selected, setSelected] = useState([]);
  const [attempts, setAttempts] = useState(
    props.data.retro ? options.length : 0
  );
  const [state, setState] = useState("null");
  const [showHint, setShowHint] = useState(false);
  const [toggle, setToggle] = useState(true);
  const [prevSelected, setPrevSelected] = useState([]);

  useEffect(() => {
    setData({ ...props.data });
    setSelected([]);
    setAttempts(props.data.retro ? options.length : 0);
    setShowHint(false);
    setState("none");
    setPrevSelected([]);

    if (!props.data.retro && data.question.includes("string_")) {
      // Until we migrate this to react-intl instead of i18n, we can doncition the translation formatting
      translateAndFormat();
    }
  }, [props.data]);

  const handleSubmit = () => {
    const goal = correctIds.length;
    let score = 0;
    for (let i = 0; i < selected.length; i++) {
      if (correctIds.includes(selected[i])) {
        score++;
      } else {
        score--;
      }
    }

    if (score === goal) {
      setState("correct");
    } else {
      if (score < 1) {
        setState("incorrect");
      } else {
        setState("partlyCorrect");
      }
    }

    setAttempts(attempts - 1);
    setPrevSelected(selected);
    setSelected([]);
    setToggle(!toggle);
  };

  const renderOption = (option, key) => {
    return (
      <div key={key} className="option-container">
        <div
          className={`
            option 
            ${selected.includes(option.id) ? "selected" : ""} 
            ${attempts < 1 || state === "correct" ? "disabled" : ""}
            ${selected.length < 1 && prevSelected.includes(option.id) ? "attempted" : ""}
            ${state}
          `}
          tabIndex="0"
          role="button"
          onClick={() => {
            if (selected.includes(option.id)) {
              const array = [...selected];
              const index = array.indexOf(option.id);
              if (index > -1) {
                array.splice(index, 1);
                setSelected(array);
                setToggle(!toggle);
              }
            } else {
              const array = [...selected];
              array.push(option.id);
              setSelected(array);
              setToggle(!toggle);
            }
          }}
          onKeyUp={(e) => {
            if (e.key === "Enter") {
              if (selected.includes(option.id)) {
                const array = [...selected];
                const index = array.indexOf(option.id);
                if (index > -1) {
                  array.splice(index, 1);
                  setSelected(array);
                  setToggle(!toggle);
                }
              } else {
                const array = [...selected];
                array.push(option.id);
                setSelected(array);
                setToggle(!toggle);
              }
            }
          }}
        >
          <div
            className={`
              alphabet
              ${state}
              ${selected.length < 1 && prevSelected.includes(option.id) ? "attempted" : ""}
            `}
          >
            {correctIds.includes(option.id) &&
            prevSelected.includes(option.id) &&
            selected.length < 1 ? (
              <div style={{ color: "white" }}>&#10004;</div>
            ) : !correctIds.includes(option.id) &&
              prevSelected.includes(option.id) &&
              selected.length < 1 ? (
              <img style={{ width: "20px" }} src={whiteX} alt="Incorrect" />
            ) : (
              t(`${(key + 1 + 9).toString(36).toUpperCase()}.string`)
            )}
          </div>
          <div className={`option-answer`}>
            {parse(t(`${option.text}.string`))}
          </div>
        </div>
      </div>
    );
  };

  const handleMSCQ_OptionSelect = (checkboxData) => {
    const newData = data;
    const checkboxes = newData.checkboxes;
    checkboxes.map((checkbox) => {
      if (checkbox.id === checkboxData.optionID) {
        if (checkbox.selected) {
          checkbox.selected = false;
        } else {
          checkbox.selected = true;
          checkboxes.map((checkbox) => {
            if (checkbox.disabled && !data.answerId.includes(checkbox.id)) {
              checkbox.selected = false;
            }
          });
        }
      }
    });
    setData({ ...newData });
  };

  const handleMSCQ_Submit = () => {
    const newData = data;
    const checkboxes = newData.checkboxes;
    const goal = data.answerId.length;
    let score = 0;
    checkboxes.map((checkbox) => {
      if (checkbox.selected) {
        checkbox.disabled = true;
        if (data.answerId.includes(checkbox.id)) {
          score++;
          checkbox.disabled = true;
        }
      }
    });
    if (score === goal) {
      data.complete = true;
    } else {
      if (data.totalAttempts > 0) {
        data.totalAttempts--;
        if (data.totalAttempts === 0) {
          data.complete = true;
        }
      }
    }
    setData({ ...newData });
  };

  const translateAndFormat = () => {
    // Translates and formats text for untranslated question widget.
    const newData = data;
    newData.question = `<p>${t(`${newData.question}.string`)}</p>`;
    newData.checkboxes.forEach((option) => {
      option.text = t(`${option.text}.string`);
      if (option.feedback !== "") {
        option.feedback = `<p>${t(`${option.feedback}.string`)}</p>`;
      }
    });
    if (newData.hintDetails.label !== "Hint") {
      newData.hintDetails.label = t(`${newData.hintDetails.label}.string`);
    }
    if (newData.hintDetails.description !== "") {
      newData.hintDetails.description = t(
        `${newData.hintDetails.description}.string`
      );
    }
    setData(newData);
    if (newData.content.includes("image")) {
      newData.image.altText = t(`${newData.image.altText}.string`);
      if (newData.image.caption) {
        newData.image.caption = `<figcaption>${t(`${newData.image.caption}.string`)}</figcaption>`;
      }
      if (newData.image.contentDropdown) {
        newData.image.contentDropdown.content = `<p>${t(`${newData.image.contentDropdown.content}.string`)}</p>`;
      }
    }
  };

  if (!props.data.retro) {
    return (
      <div className="MSQuestion-container_storybook">
        <div className={`MSQuestion_storybook`}>
          <MultiSelect
            {...data}
            selectOption={(chackboxData) => {
              handleMSCQ_OptionSelect(chackboxData);
            }}
            submitAnswer={() => {
              handleMSCQ_Submit();
            }}
            preview={props.preview}
          />
        </div>
      </div>
    );
  } else {
    return (
      <div className="MSQuestion-container">
        <div
          className={`MSQuestion ${attempts === 0 || state === "correct" ? "complete" : ""}`}
        >
          <div>
            <div className="question-header">{t(`MSQ.string`)}</div>
            <div className="question">
              {parse(t(`${data.question}.string`))}
            </div>
          </div>
          {image !== null || image !== "" ? (
            <img
              className="question-image"
              style={{
                minHeight: props.preview && image !== "" ? "100px" : null,
              }}
              src={imgSrc}
              onError={() =>
                setImgSrc(
                  window.location.origin +
                    window.location.pathname +
                    `locales/en/images/${image}`
                )
              }
              alt={data.imageAlt !== "" ? t(`${data.imageAlt}.string`) : ""}
            />
          ) : null}
          <div className="options">
            {options.map((option, key) => {
              return renderOption(option, key);
            })}
          </div>
          <div
            className={`
              MSQfeedback feedback ${state} ${state !== "none" ? "attempted" : null}
            `}
          >
            {t(`Feedback.string`)}
            <br />
            {state === "correct"
              ? parse(t(`${data.correctMsg}.string`))
              : state === "incorrect"
                ? parse(t(`${data.incorrectMsg}.string`))
                : parse(t(`${data.partlyCorrectMsg}.string`))}
          </div>
          <div
            className="hint-container"
            tabIndex="0"
            role="button"
            onClick={() => {
              setShowHint(!showHint);
            }}
            onKeyUp={(e) => {
              const keycode = e.keyCode ? e.keyCode : e.which;
              if (keycode === 13) {
                setShowHint(!showHint);
              }
            }}
            style={{ display: data.hint === "" ? "none" : null }}
          >
            <div className="hint-toggle">
              {t(`Hint.string`)}
              <img
                className={`hint-arrow ${!showHint ? "hide" : ""}`}
                src={Arrow}
                alt=""
                role="presentation"
              />
            </div>
            <div className={`hint ${!showHint ? "hide" : ""}`}>
              {parse(t(`${data.hint}.string`))}
            </div>
          </div>
          <div className="submit-container">
            <div className="attempts">
              {t(`AttemptsLeft.string`)}: {attempts}
            </div>
            <div
              className={`submit ${selected.length === 0 || attempts < 1 || state === "correct" ? "disabled" : ""}`}
              tabIndex="0"
              role="button"
              onClick={() => {
                handleSubmit();
              }}
              onKeyUp={(e) => {
                const keycode = e.keyCode ? e.keyCode : e.which;
                if (keycode === 13 && attempts > 0 && state !== "correct") {
                  handleSubmit();
                }
              }}
            >
              {t(`SubmitAnswer.string`)}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
